import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
    Package, 
    Search,
    AlertTriangle,
    CheckCircle,
    Clock,
    Truck,
    FileText,
    Download,
    Send,
    ArrowLeft,
    RefreshCw,
    CreditCard,
    Mail,
    ShoppingBag,
    Plus,
    X,
    Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SAVPage = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('new');
    const [loading, setLoading] = useState(false);
    const [savRequests, setSavRequests] = useState([]);
    
    // Search mode: 'imei' or 'orders'
    const [searchMode, setSearchMode] = useState('orders');
    
    // IMEI search
    const [imeiSearch, setImeiSearch] = useState('');
    const [imeiResult, setImeiResult] = useState(null);
    const [searchError, setSearchError] = useState(null);
    
    // Orders list
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    
    // Selected items for SAV (can be from multiple orders)
    const [selectedItems, setSelectedItems] = useState([]);
    
    // IMEIs that already have an active SAV
    const [imeisWithSav, setImeisWithSav] = useState([]);
    
    // SAV form
    const [submitting, setSubmitting] = useState(false);
    
    // Transport label option
    const [wantsTransportLabel, setWantsTransportLabel] = useState(false);

    const fetchSavRequests = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/sav/my-requests`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavRequests(response.data.sav_requests || []);
        } catch (error) {
            console.error('Fetch SAV error:', error);
        }
    }, [token]);

    const fetchOrders = useCallback(async () => {
        setLoadingOrders(true);
        try {
            const response = await axios.get(`${API_URL}/orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Filter only shipped/delivered orders
            const eligibleOrders = (response.data.orders || []).filter(
                o => ['shipped', 'delivered'].includes(o.status)
            );
            setOrders(eligibleOrders);
            
            // Collect all IMEIs from these orders
            const allImeis = eligibleOrders.flatMap(
                order => (order.items || []).map(item => item.imei).filter(Boolean)
            );
            
            // Check which IMEIs already have SAV
            if (allImeis.length > 0) {
                try {
                    const savResponse = await axios.post(
                        `${API_URL}/sav/check-existing`,
                        allImeis,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setImeisWithSav(savResponse.data.imeis_with_sav || []);
                } catch (err) {
                    console.error('Check existing SAV error:', err);
                }
            }
        } catch (error) {
            console.error('Fetch orders error:', error);
        } finally {
            setLoadingOrders(false);
        }
    }, [token]);

    useEffect(() => {
        fetchSavRequests();
        fetchOrders();
    }, [fetchSavRequests, fetchOrders]);

    const handleSearchIMEI = async () => {
        if (!imeiSearch || imeiSearch.length < 8) {
            toast.error('Veuillez entrer un IMEI valide');
            return;
        }

        setLoading(true);
        setImeiResult(null);
        setSearchError(null);

        try {
            const response = await axios.get(
                `${API_URL}/sav/check-eligibility/${imeiSearch}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setImeiResult(response.data);
        } catch (error) {
            setSearchError(error.response?.data?.detail || 'Erreur lors de la recherche');
        } finally {
            setLoading(false);
        }
    };

    const addItemFromIMEI = () => {
        if (!imeiResult) return;
        
        // Check if already added
        if (selectedItems.some(i => i.imei === imeiResult.imei)) {
            toast.error('Cet appareil est déjà dans la liste');
            return;
        }
        
        setSelectedItems([...selectedItems, {
            imei: imeiResult.imei,
            order_id: imeiResult.order_id,
            item: imeiResult.item,
            is_eligible: imeiResult.is_eligible,
            days_remaining: imeiResult.days_remaining,
            delivery_date: imeiResult.delivery_date,
            reason: ''
        }]);
        
        setImeiResult(null);
        setImeiSearch('');
        toast.success('Appareil ajouté à la demande');
    };

    const toggleItemSelection = async (order, item) => {
        const existing = selectedItems.find(i => i.imei === item.imei);
        
        if (existing) {
            // Remove
            setSelectedItems(selectedItems.filter(i => i.imei !== item.imei));
        } else {
            // Check eligibility first
            try {
                const response = await axios.get(
                    `${API_URL}/sav/check-eligibility/${item.imei}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                
                setSelectedItems([...selectedItems, {
                    imei: item.imei,
                    order_id: order.id,
                    item: {
                        brand: item.brand,
                        model: item.model,
                        storage_capacity: item.storage_capacity,
                        color: item.color,
                        grade: item.grade,
                        price: item.price
                    },
                    is_eligible: response.data.is_eligible,
                    days_remaining: response.data.days_remaining,
                    delivery_date: response.data.delivery_date,
                    reason: ''
                }]);
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Impossible de vérifier cet appareil');
            }
        }
    };

    const removeSelectedItem = (imei) => {
        setSelectedItems(selectedItems.filter(i => i.imei !== imei));
    };

    const updateItemReason = (imei, newReason) => {
        setSelectedItems(selectedItems.map(i => 
            i.imei === imei ? { ...i, reason: newReason } : i
        ));
    };

    // Check if all selected items have reasons
    const allItemsHaveReason = selectedItems.length > 0 && selectedItems.every(i => i.reason.trim());

    const handleSubmitSAV = async () => {
        if (selectedItems.length === 0) {
            toast.error('Veuillez sélectionner au moins un appareil');
            return;
        }
        if (!allItemsHaveReason) {
            toast.error('Veuillez indiquer la raison pour chaque appareil');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(
                `${API_URL}/sav/create`,
                {
                    items: selectedItems.map(i => ({
                        imei: i.imei,
                        order_id: i.order_id,
                        reason: i.reason
                    })),
                    wants_transport_label: wantsTransportLabel
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Demande SAV soumise avec succès');
            setSelectedItems([]);
            setWantsTransportLabel(false);
            setActiveTab('history');
            fetchSavRequests();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
        } finally {
            setSubmitting(false);
        }
    };

    const handleMarkShipped = async (savId) => {
        try {
            await axios.post(
                `${API_URL}/sav/${savId}/mark-shipped`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Retour marqué comme expédié');
            fetchSavRequests();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleDownloadLabel = async (savId) => {
        try {
            const response = await axios.get(
                `${API_URL}/sav/${savId}/return-label`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bon_transport_sav_${savId.slice(0, 8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Bon de transport téléchargé');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Bon de transport non disponible');
        }
    };

    const handleDownloadSAVSlip = async (savId) => {
        try {
            const response = await axios.get(
                `${API_URL}/sav/${savId}/sav-slip`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bon_sav_${savId.slice(0, 8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Bon de SAV téléchargé');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Bon de SAV non disponible');
        }
    };

    const handleDownloadCreditNote = async (savId) => {
        try {
            const response = await axios.get(
                `${API_URL}/sav/${savId}/credit-note`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `avoir_${savId.slice(0, 8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Avoir téléchargé');
        } catch (error) {
            toast.error('Avoir non disponible');
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pending': { color: 'bg-amber-100 text-amber-800', icon: Clock, label: 'En attente' },
            'approved': { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, label: 'Bon de transport disponible' },
            'shipped_back': { color: 'bg-indigo-100 text-indigo-800', icon: Truck, label: 'Retour expédié' },
            'received': { color: 'bg-purple-100 text-purple-800', icon: Package, label: 'Reçu' },
            'processing': { color: 'bg-orange-100 text-orange-800', icon: RefreshCw, label: 'En cours d\'examen' },
            'refunded_credit': { color: 'bg-green-100 text-green-800', icon: CreditCard, label: 'Crédit accordé' },
            'refunded_transfer': { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle, label: 'Remboursé' },
            'rejected': { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'Refusé' }
        };

        const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status };
        const Icon = config.icon;

        return (
            <Badge className={config.color}>
                <Icon className="w-3 h-3 mr-1" />
                {config.label}
            </Badge>
        );
    };

    const allEligible = selectedItems.length > 0 && selectedItems.every(i => i.is_eligible);
    const hasIneligible = selectedItems.some(i => !i.is_eligible);
    const totalValue = selectedItems.reduce((sum, i) => sum + (i.item?.price || 0), 0);

    return (
        <Layout>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Service Après-Vente
                        </h1>
                        <p className="text-slate-600 mt-1">
                            Garantie 5 jours ouvrés après réception
                        </p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/orders')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Mes commandes
                    </Button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    <Button
                        variant={activeTab === 'new' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('new')}
                    >
                        Nouvelle demande
                    </Button>
                    <Button
                        variant={activeTab === 'history' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('history')}
                    >
                        Mes demandes ({savRequests.length})
                    </Button>
                </div>

                {activeTab === 'new' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left: Selection */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Search mode selector */}
                            <Card className="border-slate-200">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg">Sélectionner les appareils</CardTitle>
                                    <CardDescription>
                                        Choisissez les appareils à retourner par commande ou par IMEI
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex gap-2 mb-4">
                                        <Button
                                            variant={searchMode === 'orders' ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setSearchMode('orders')}
                                        >
                                            <ShoppingBag className="w-4 h-4 mr-2" />
                                            Par commande
                                        </Button>
                                        <Button
                                            variant={searchMode === 'imei' ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setSearchMode('imei')}
                                        >
                                            <Search className="w-4 h-4 mr-2" />
                                            Par IMEI
                                        </Button>
                                    </div>

                                    {/* IMEI Search */}
                                    {searchMode === 'imei' && (
                                        <div className="space-y-4">
                                            <div className="flex gap-3">
                                                <Input
                                                    placeholder="Entrez l'IMEI"
                                                    value={imeiSearch}
                                                    onChange={(e) => {
                                                        // Allow alphanumeric and dash for TEMP-XXXXXXXX format
                                                        const cleaned = e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 20);
                                                        setImeiSearch(cleaned);
                                                    }}
                                                    onPaste={(e) => {
                                                        e.preventDefault();
                                                        const pastedText = e.clipboardData.getData('text').trim();
                                                        // Allow alphanumeric and dash for TEMP-XXXXXXXX format
                                                        const cleaned = pastedText.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 20);
                                                        setImeiSearch(cleaned);
                                                    }}
                                                    className="flex-1 font-mono"
                                                    data-testid="imei-search-input"
                                                />
                                                <Button onClick={handleSearchIMEI} disabled={loading}>
                                                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                                </Button>
                                            </div>

                                            {searchError && (
                                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                                    <p className="text-red-800 text-sm flex items-center gap-2">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        {searchError}
                                                    </p>
                                                </div>
                                            )}

                                            {imeiResult && (
                                                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <h4 className="font-semibold">{imeiResult.item.brand} {imeiResult.item.model}</h4>
                                                            <p className="text-sm text-slate-600">{imeiResult.item.storage_capacity}GB • {imeiResult.item.color}</p>
                                                            <p className="text-xs font-mono text-slate-500 mt-1">IMEI: {imeiResult.imei}</p>
                                                            <div className={`mt-2 text-sm ${imeiResult.is_eligible ? 'text-green-600' : 'text-amber-600'}`}>
                                                                {imeiResult.is_eligible ? (
                                                                    <span className="flex items-center gap-1">
                                                                        <CheckCircle className="w-4 h-4" />
                                                                        {imeiResult.days_remaining} jours restants
                                                                    </span>
                                                                ) : (
                                                                    <span className="flex items-center gap-1">
                                                                        <AlertTriangle className="w-4 h-4" />
                                                                        Hors garantie
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-bold">{imeiResult.item.price?.toFixed(2)} €</p>
                                                            <Button size="sm" className="mt-2" onClick={addItemFromIMEI}>
                                                                <Plus className="w-4 h-4 mr-1" />
                                                                Ajouter
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Orders list */}
                                    {searchMode === 'orders' && (
                                        <div className="space-y-4 max-h-[500px] overflow-y-auto">
                                            {loadingOrders ? (
                                                <div className="text-center py-8">
                                                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                                                </div>
                                            ) : orders.length === 0 ? (
                                                <div className="text-center py-8 text-slate-500">
                                                    Aucune commande éligible
                                                </div>
                                            ) : (
                                                orders.map(order => (
                                                    <div key={order.id} className="border rounded-lg p-4">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div>
                                                                <span className="font-mono text-sm font-medium">#{order.id.slice(0, 8)}</span>
                                                                <span className="text-slate-500 text-sm ml-2">
                                                                    {new Date(order.created_at).toLocaleDateString('fr-FR')}
                                                                </span>
                                                            </div>
                                                            <Badge className="bg-slate-100 text-slate-700">
                                                                {order.items?.length || 0} article(s)
                                                            </Badge>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {order.items?.map(item => {
                                                                const isSelected = selectedItems.some(i => i.imei === item.imei);
                                                                const hasSav = imeisWithSav.includes(item.imei);
                                                                return (
                                                                    <div 
                                                                        key={item.imei}
                                                                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                                                            hasSav 
                                                                                ? 'bg-slate-100 border border-slate-200 cursor-not-allowed opacity-60' 
                                                                                : isSelected 
                                                                                    ? 'bg-blue-50 border border-blue-200 cursor-pointer' 
                                                                                    : 'bg-slate-50 hover:bg-slate-100 cursor-pointer'
                                                                        }`}
                                                                        onClick={() => !hasSav && toggleItemSelection(order, item)}
                                                                    >
                                                                        <Checkbox checked={isSelected} disabled={hasSav} />
                                                                        <Smartphone className={`w-5 h-5 ${hasSav ? 'text-slate-400' : 'text-slate-400'}`} />
                                                                        <div className="flex-1">
                                                                            <p className="font-medium text-sm">{item.brand} {item.model}</p>
                                                                            <p className="text-xs text-slate-500">{item.storage_capacity}GB • IMEI: {item.imei}</p>
                                                                        </div>
                                                                        {hasSav ? (
                                                                            <Badge className="bg-slate-200 text-slate-600 text-xs">
                                                                                Déjà traité en SAV
                                                                            </Badge>
                                                                        ) : (
                                                                            <span className="font-semibold text-sm">{item.price?.toFixed(2)} €</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Right: Summary & Submit */}
                        <div className="space-y-4">
                            {/* Selected items */}
                            <Card className="border-slate-200">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg flex items-center justify-between">
                                        <span>Appareils sélectionnés</span>
                                        <Badge>{selectedItems.length}</Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {selectedItems.length === 0 ? (
                                        <p className="text-slate-500 text-sm text-center py-4">
                                            Aucun appareil sélectionné
                                        </p>
                                    ) : (
                                        <div className="space-y-4">
                                            {selectedItems.map(item => (
                                                <div key={item.imei} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex-1">
                                                            <p className="font-medium text-sm">{item.item?.brand} {item.item?.model}</p>
                                                            <p className="text-xs text-slate-500">{item.imei}</p>
                                                            {!item.is_eligible && (
                                                                <Badge className="bg-amber-100 text-amber-800 text-xs mt-1">
                                                                    Hors garantie
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-sm">{item.item?.price?.toFixed(2)} €</span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 w-6 p-0"
                                                                onClick={() => removeSelectedItem(item.imei)}
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <Textarea
                                                        placeholder="Raison du retour pour cet appareil..."
                                                        value={item.reason}
                                                        onChange={(e) => updateItemReason(item.imei, e.target.value)}
                                                        rows={2}
                                                        className="text-sm"
                                                    />
                                                </div>
                                            ))}
                                            <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                                                <span>Total</span>
                                                <span>{totalValue.toFixed(2)} €</span>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Warning if out of warranty */}
                            {hasIneligible && (
                                <Card className="border-amber-200 bg-amber-50">
                                    <CardContent className="py-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-amber-800 font-medium">
                                                    Certains appareils sont hors garantie
                                                </p>
                                                <p className="text-xs text-amber-700 mt-1">
                                                    La demande sera tout de même soumise mais ces appareils seront examinés au cas par cas.
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Submit Button */}
                            {selectedItems.length > 0 && (
                                <Card className="border-slate-200">
                                    <CardContent className="py-4 space-y-4">
                                        {/* Transport label option */}
                                        <div 
                                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                                wantsTransportLabel 
                                                    ? 'bg-blue-50 border-blue-300' 
                                                    : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                                            }`}
                                            onClick={() => setWantsTransportLabel(!wantsTransportLabel)}
                                        >
                                            <div className="flex items-start gap-3">
                                                <Checkbox 
                                                    checked={wantsTransportLabel} 
                                                    onCheckedChange={setWantsTransportLabel}
                                                />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-slate-800">
                                                        Je souhaite recevoir un bon de transport prépayé
                                                    </p>
                                                    <p className="text-xs text-slate-500 mt-1">
                                                        {wantsTransportLabel 
                                                            ? "Un bon de transport sera mis à votre disposition après validation de la demande."
                                                            : "L'expédition sera à votre charge si vous ne cochez pas cette option."
                                                        }
                                                    </p>
                                                </div>
                                                <Truck className={`w-5 h-5 ${wantsTransportLabel ? 'text-blue-600' : 'text-slate-400'}`} />
                                            </div>
                                        </div>
                                        
                                        <Button
                                            onClick={handleSubmitSAV}
                                            disabled={submitting || !allItemsHaveReason}
                                            className="w-full"
                                            data-testid="submit-sav-btn"
                                        >
                                            {submitting ? (
                                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Send className="w-4 h-4 mr-2" />
                                            )}
                                            Soumettre la demande ({selectedItems.length} appareil{selectedItems.length > 1 ? 's' : ''})
                                        </Button>
                                        {!allItemsHaveReason && selectedItems.length > 0 && (
                                            <p className="text-xs text-amber-600 mt-2 text-center">
                                                Veuillez indiquer la raison pour chaque appareil
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Contact info */}
                            <Card className="border-slate-200 bg-slate-50">
                                <CardContent className="py-4">
                                    <div className="flex items-start gap-3">
                                        <Mail className="w-5 h-5 text-slate-500" />
                                        <div>
                                            <p className="text-sm font-medium text-slate-700">
                                                Besoin d'aide ?
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                Contactez-nous à{' '}
                                                <a href="mailto:rma@phoneb2b.com" className="text-blue-600 underline">
                                                    rma@phoneb2b.com
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="space-y-4">
                        {savRequests.length === 0 ? (
                            <Card className="border-slate-200">
                                <CardContent className="py-12 text-center">
                                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                                    <p className="text-slate-600">Aucune demande SAV</p>
                                    <Button 
                                        variant="outline" 
                                        className="mt-4"
                                        onClick={() => setActiveTab('new')}
                                    >
                                        Créer une demande
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            savRequests.map((sav) => (
                                <Card key={sav.id} className="border-slate-200">
                                    <CardContent className="py-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <span className="font-mono text-sm text-slate-500">
                                                        #{sav.id.slice(0, 8)}
                                                    </span>
                                                    {getStatusBadge(sav.status)}
                                                </div>
                                                
                                                {/* Items list */}
                                                {sav.items && sav.items.length > 0 ? (
                                                    <div className="space-y-2 mb-2">
                                                        {sav.items.map((item, idx) => (
                                                            <div key={idx} className="p-2 bg-slate-50 rounded">
                                                                <p className="text-sm">
                                                                    <span className="font-medium">{item.item?.brand} {item.item?.model}</span>
                                                                    <span className="text-slate-500 ml-2">IMEI: {item.imei}</span>
                                                                </p>
                                                                {item.reason && (
                                                                    <p className="text-xs text-slate-600 mt-1 italic">
                                                                        "{item.reason}"
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : sav.item ? (
                                                    <div className="mb-2">
                                                        <h4 className="font-semibold text-slate-900">
                                                            {sav.item?.brand} {sav.item?.model}
                                                        </h4>
                                                        <p className="text-sm text-slate-600">
                                                            {sav.item?.storage_capacity}GB • IMEI: {sav.imei}
                                                        </p>
                                                        {sav.reason && (
                                                            <p className="text-sm text-slate-500 mt-2 italic">
                                                                "{sav.reason}"
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : null}
                                                
                                                <p className="text-xs text-slate-400 mt-2">
                                                    Créé le {new Date(sav.created_at).toLocaleDateString('fr-FR')}
                                                </p>
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                {/* Bon de SAV (always available when approved) */}
                                                {['approved', 'partial', 'shipped_back', 'received', 'processing'].includes(sav.status) && sav.sav_slip_url && (
                                                    <Button 
                                                        variant="default" 
                                                        size="sm"
                                                        onClick={() => handleDownloadSAVSlip(sav.id)}
                                                        className="bg-blue-600 hover:bg-blue-700"
                                                    >
                                                        <FileText className="w-4 h-4 mr-1" />
                                                        Bon de SAV
                                                    </Button>
                                                )}

                                                {/* Bon de transport (optional) */}
                                                {['approved', 'partial', 'shipped_back'].includes(sav.status) && sav.return_label_url && (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleDownloadLabel(sav.id)}
                                                    >
                                                        <Download className="w-4 h-4 mr-1" />
                                                        Bon transport
                                                    </Button>
                                                )}

                                                {['approved', 'partial'].includes(sav.status) && (
                                                    <Button 
                                                        size="sm"
                                                        onClick={() => handleMarkShipped(sav.id)}
                                                    >
                                                        <Truck className="w-4 h-4 mr-1" />
                                                        J'ai expédié
                                                    </Button>
                                                )}

                                                {['refunded_credit', 'refunded_transfer'].includes(sav.status) && sav.credit_note_id && (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleDownloadCreditNote(sav.id)}
                                                    >
                                                        <FileText className="w-4 h-4 mr-1" />
                                                        Avoir
                                                    </Button>
                                                )}

                                                {sav.status === 'refunded_credit' && sav.credit_amount && (
                                                    <div className="text-right">
                                                        <p className="text-xs text-slate-500">Crédit</p>
                                                        <p className="font-bold text-green-600">
                                                            +{sav.credit_amount.toFixed(2)} €
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {sav.admin_notes && (
                                            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                                                <p className="text-sm text-slate-700">
                                                    <span className="font-medium">Note:</span> {sav.admin_notes}
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                )}
            </div>
        <Footer />
        </Layout>
    );
};

export default SAVPage;
